import ObjectIndex from "./context_index/ObjectIndex.js";
import ConfigurationIndex from "./context_index/ConfigurationIndex.js";
import Projection from "@/prototype/Projection.js";
import RoomArea from "../../ui/RoomArea.js";

import Dexie from "dexie";

import uuid from "@/library/uuid.js";
import global from "@/prototype/global.js";

import {CONTEXT_DATABASE_VERSION} from "../constants.js";

import Hooks from "./Hooks.js";

global.dexie = Dexie;

const log = global.log.getLogger("f_mtac.storage.space.context");
log.setLevel(log.levels.WARN);

const data_store_name = ({space_index, desktop_index}) => `context/${space_index}/${desktop_index}`;
const data_store = ({space_index, desktop_index}) => {
    let name = data_store_name({space_index, desktop_index});
    let store = global.configuration[name];
    if (store !== undefined) {
        return store;
    }
    return null;
};
const database_name = ({space_index, desktop_index}) => `f_mtac.storage.${space_index}.${desktop_index}`;

const Context = function (space, desktop_index) {
    if (typeof space !== "object") {
        throw "wrong space type, shall be an object";
    }
    if (typeof desktop_index !== "number") {
        throw "wrong desktop_index type, shall be a number";
    }
    var database = new Dexie(database_name({space_index: space.index, desktop_index}), {autoOpen: false});
    var room = null;
    var object_index = new ObjectIndex(this);
    var configuration_index = new ConfigurationIndex(this);
    var projection = new Projection(this);
    var context_data_store = null;
    var context_data = null;
    var context_runtime_data = {};
    var hooks = new Hooks();
    database.version(CONTEXT_DATABASE_VERSION - 1).stores({
        objects: "local_id,&remote_id,&object_hash,object_data.attributes.dataUser,object_data.attributes.dataProjectionPoint,object_data.attributes.dataMedia,object_data.attributes.dataType,object_data.attributes.dataSubtype",
        configurations: "name,hash"
    });
    database
        .version(CONTEXT_DATABASE_VERSION)
        .stores({
        objects: "local_id,&remote_id,&object_hash,object_data.attributes.dataUser,object_data.attributes.dataProjectionPoint,object_data.attributes.dataMedia,object_data.attributes.dataType,object_data.attributes.dataSubtype",
        configurations: "name,hash"
        })
        .upgrade(async (tx) => {
            await tx
                .table("objects")
                .toCollection()
                .modify((_, ref) => {
            delete ref.value;
        });
            await tx
                .table("configurations")
                .toCollection()
                .modify((_, ref) => {
            delete ref.value;
        });
    });
    hooks.add("objects", this);
    hooks.add("configurations", this);

    Object.defineProperty(this, "room_id", {
        set: () => {},
        get: () => (room !== null ? room.room_id : null)
    });

    Object.defineProperty(this, "room", {
        set: () => {},
        get: () => room
    });

    Object.defineProperty(this, "data", {
        set: () => {},
        get: () => context_data
    });

    Object.defineProperty(this, "runtime_data", {
        set: () => {},
        get: () => context_runtime_data
    });

    Object.defineProperty(this, "space", {
        set: () => {},
        get: () => space
    });

    Object.defineProperty(this, "index", {
        set: () => {},
        get: () => desktop_index
    });

    Object.defineProperty(this, "objects", {
        set: () => {},
        get: () => database.objects
    });

    Object.defineProperty(this, "remote_global_hash", {
        set: () => {},
        get: () => {
            if (object_index === null) {
                return null;
            }
            return object_index.remote_global_hash;
        }
    });

    /**
     * @brief Find an object with a given param and a given value
     * @param {*} param 
     * @param {*} value 
     * @returns the object if found
     */
    this.find_object_by_param = async (param, value) => {
        log.debug(`Context.find_object_by_param in param= ${param}, value = ${value}`);
                
        let object = await database.objects.where(param).equals(value).first();
        log.debug("Context.find_object_by_param out", object);
        return object;
    };

    const get_map_object_attribute = async (attribute) => {
        //log.info("get_map_object_attribute", {attribute});
        try {
            var map_object = await this.object_path({
                key: "object_data.attributes.dataSubtype"
            })
                .equals("background_image")
                .first();
        } catch (error) {
            log.debug("Context.get_map_object_attribute Error ", {error});
            // AGPOC-5726 add catch
            return null;
        }
        //log.info("Context.get_map_object_attribute ...");
        if (map_object === undefined) {
            log.info("Context.get_map_object_attribute background_image === undefined");
            return null;
        }
        //log.debug("Context.get_map_object_attribute background_image.object_data", map_object.object_data);
        return map_object.object_data.attributes[attribute];
    };

    const set_map_object_attribute = async (attribute, value) => {
        log.info("Context.set_map_object_attribute", {attribute, value});
        var map_object_attribute = {};
        map_object_attribute[`object_data.attributes.${attribute}`] = value;
        await this.object_update_path(
            {
            "object_data.attributes.dataSubtype": "background_image"
            },
            map_object_attribute
        );
    };

    Object.defineProperty(this, "name", {
        set: (name) => {
            set_map_object_attribute("dataName", name);
        },
        get: () => get_map_object_attribute("dataName")
    });

    Object.defineProperty(this, "map_media_id", { // ex background_image
        set: () => {},
        get: () => get_map_object_attribute("dataMedia")
    });

    Object.defineProperty(this, "type", {
        set: () => {},
        get: () => get_map_object_attribute("dataType")
    });

    Object.defineProperty(this, "projection_data", {
        /* setter does not support async
        set: (value) => {
            set_map_object_attribute("dataProjection", value);
        },*/
        get: () => get_map_object_attribute("dataProjection")
    });

    /**
     * Set projection data in media Objects with async
     * @param {*} data 
     */
    this.set_projection_data = async (data) => {
        await set_map_object_attribute("dataProjection", data);
    };

    Object.defineProperty(this, "projection", {
        set: () => {},
        get: () => projection
    });

    Object.defineProperty(this, "grid_data", {
        set: (data) => {
            set_map_object_attribute("dataGrid", data);
        },
        get: () => get_map_object_attribute("dataGrid")
    });

    Object.defineProperty(this, "hooks", {
        set: () => {},
        get: () => hooks
    });

    this.is_empty = async () => {
        if ((await this.map_media_id) === null) {
            return true;
        }
        return false;
    };

    this.data_commit = () => context_data_store.save();
    this.pull = async function () {
        log.debug("Context.pull");
        if (room === null) {
            return;
        }
        await configuration_index.pull();
        await object_index.pull();
    };
    this.push = async function () {
        //log.debug("Context.push");
        if (room === null) {
            return;
        }
        await object_index.push();
        await configuration_index.push();
    };
    this.revert = async function () {
        log.debug("Context.revert");
        if (room === null) {
            return;
        }
        await object_index.revert();
        await configuration_index.revert();
    };
    this.share = async function (new_room) {
        log.debug("Context.share");
        let old_room = room;
        if (new_room === undefined) {
            new_room = old_room;
        }
        // in case of server restart, need to re-commit everything on the same room
        if (String(new_room) === String(old_room)) {
            return;
        }
        if (old_room !== null) {
            await this.unshare();
        }
        await context_data_store.init(new_room);
        await Promise.all([object_index.init(new_room), configuration_index.init(new_room)]);
        room = new_room;
    };
    this.unshare = async function () {
        log.debug("Context.unshare");
        room = null;
        context_data_store.deinit();
        await Promise.all([object_index.deinit(), configuration_index.deinit()]);
    };
    let object_set_data = (object, data) => {
        log.debug("Context.object_set_data");
        let object_data_type = typeof data;
        switch (object_data_type) {
        case "object":
            object.object_data = data;
            break;
        case "string":
            object.object_data = JSON.parse(data);
            break;
        default:
            throw "object data shall be of type string or object";
        }
    };
    this.create_object_in_database = async function ({local_id, remote_id, object_data, object_hash}) {
        log.debug("Context.create_object_in_database", {local_id, remote_id, object_data, object_hash});
        let new_object = {};
        if (object_data === undefined) {
            throw "object data shall be defined";
        }
        if (local_id === undefined) {
            local_id = `local-${uuid()}`;
        }
        new_object.local_id = local_id;
        if (remote_id !== undefined) {
            new_object.remote_id = remote_id;
            if (object_hash === undefined) {
                throw "with a remote id object hash shall be defined";
            }
        }
        if (object_hash !== undefined) {
            new_object.object_hash = object_hash;
        }

        object_set_data(new_object, object_data);
        if (new_object.object_data.attributes && new_object.object_data.attributes.dataMedia) {
            space.media_retrieve({local_id: new_object.object_data.attributes.dataMedia});
        }

        var space_is_empty = false;
        if (global.storage.selected_space){
            space_is_empty = await global.storage.selected_space.is_empty();
        }
        log.debug("Context.create_object_in_database update database");
        return database
            .transaction("rw", database.objects, database.configurations, () => {
                log.debug("Context.create_object_in_database database.transaction ", {local_id, remote_id});
                let changeset = object_index.commit({
                        local_id,
                        remote_id,
                    old_hash: null,
                    old_data: null,
                    new_hash: new_object.object_hash,
                    new_data: new_object.object_data
                });
                if (changeset !== null) {
                    new_object.object_hash = changeset.new_hash;
                }
                log.debug("Context.create_object_in_database objects =", new_object);
                return database.objects.put(new_object);  
            })
            .catch((error) => {
                log.error("Context.create_object_in_database error1: ", error);
                switch (error.name) {
                case Dexie.errnames.Constraint:
                    return new_object; // ignoring Dexie constraint errors
                default:
                    throw error;
                }
            })
            .then(() => {
                // AGPOC-3479 : Archive date creation
                // lorsqu'on crée un objet de type background image, tester si le space est vide.
                // S'il est vide => mettre à jour la date de création = date de création de la 1ere sitac
                if (space_is_empty){
                    global.storage.selected_space.created = Date.now();
                    global.storage.selected_space.data_commit().then(() => {
                        RoomArea.refreshSpace(global.storage.selected_space.index);
                    });
                }
                if (remote_id === undefined) {
                    //if the room is not shared, remote_id is undefined
                    log.debug("Context.create_object_in_database remote_id undefined ");
                    return space.push();
                }
            })
        .then(() => new_object);
    };

    this.delete_object_in_database = function ({local_id, remote_id}) {
        log.debug("Context.delete_object_in_database", {local_id, remote_id});
        let old_object = {};
        let objects = database.objects;
        log.debug("Context.delete_object_in_database update database");
        return database
            .transaction("rw", database.objects, database.configurations, () => {
                if (local_id !== undefined) {
                    objects = objects.where("local_id").equals(local_id);
                } else if (remote_id !== undefined) {
                    objects = objects.where("remote_id").equals(remote_id);
                } else {
                    throw "local id or remote id must be defined";
                }
                return objects.modify((object, ref) => {
                    Object.assign(old_object, object);
                    object_index.commit({
                            local_id,
                            remote_id,
                            base_remote_id: old_object.remote_id,
                        old_hash: old_object.object_hash,
                        old_data: old_object.object_data,
                        new_data: null,
                        new_hash: null
                    });
                    delete ref.value;
                });
            })
            .catch((error) => {
                log.error("object delete error: ", error);
                throw error;
            })
            .then((modified) => {
                if (modified === 0) {
                    log.error(`no objects has been deleted with id local_id = ${local_id} and remote_id = ${remote_id}`);
                    return;
                }
                log.debug("Context.delete_object_in_database", old_object.object_data.attributes.dataMedia);
                if (old_object.object_data.attributes.dataMedia !== undefined) {
                    global.storage.selected_space.media_delete({local_id: old_object.object_data.attributes.dataMedia});
                }
                log.debug("Context.delete_object_in_database remote_id ", remote_id);
                if (remote_id === undefined) {
                    return space.push();
                }
            })
            .then(() => old_object);
    };

    this.object_agnet_delete = function ({local_id}) {
        log.debug("Context.object_agnet_delete", {local_id});
        let old_object = {};
        let objects = database.objects;
        return database
            .transaction("rw", database.objects, database.configurations, () => {
                if (local_id !== undefined) {
                    objects = objects.where("local_id").equals(local_id);
                } else {
                    throw "local id or remote id must be defined";
                }
                return objects.modify((object, ref) => {
                    Object.assign(old_object, object);
                    delete ref.value;
                });
                })
                .catch((error) => {
                log.error("Context.object_agnet_delete error: ", error);
                throw error;
            })
            .then((modified) => {
                if (modified === 0) {
                    log.error(`Context.object_agnet_delete no objects has been deleted with id local_id = ${local_id}`);
                    return;
                }
            })
            .then(() => old_object);
    };

    /**
     * @brief write object in Dexie without waiting for the result (not async)
     * @param {*} param0 
     * @returns 
     */
    this.update_object_in_database = function ({local_id, remote_id, object_data, object_hash}) {
        log.debug(`Context.update_object_in_database in desktop_id = ${desktop_index}`);
        log.debug("Context.update_object_in_database", {local_id, remote_id, object_data, object_hash});
        let new_object = {};
        let objects = database.objects;
        if (local_id !== undefined) {
            objects = objects.where("local_id").equals(local_id);
        } else if (remote_id !== undefined) {
            objects = objects.where("remote_id").equals(remote_id);
        }
        if (remote_id !== undefined) {
            new_object.remote_id = remote_id;
        }
        if (object_hash !== undefined) {
            new_object.object_hash = object_hash;
        }
        if (object_data !== undefined) {
            object_set_data(new_object, object_data);
        }

        // AGPOC-6761 : media may be never created on remote client because when receiving first object creation, dataMedia is still fake 010203040506070809...
        // or retrieve media list doesn't return new one because not yet created on sever
        // => so retrieve media list from server even when receiving background image object update
        if (new_object.object_data && new_object.object_data.attributes.dataMedia) {
            space.media_retrieve({local_id: new_object.object_data.attributes.dataMedia});
        }

        let old_object = {};
        log.debug("Context.update_object_in_database start transaction");
        return database
            .transaction("rw", database.objects, database.configurations, () => {
                
                return objects.modify((object) => {
                    log.debug("Context.update_object_in_database.transaction.objects.modify in", {old_object, object});
                    Object.assign(old_object, object);
                    if ("object_hash" in old_object && object.remote_id === remote_id && old_object.object_hash === new_object.object_hash) {
                        Object.assign(new_object, object);
                        log.warn(`Context.update_object_in_database.transaction.objects.modify out: no update received for object ${old_object.local_id} (remote id ${old_object.remote_id})`);
                        return;
                    }
                    Object.assign(object, new_object);
                    Object.assign(new_object, object);
                    if (object_data !== undefined) {
                        let changeset = object_index.commit({
                            local_id: object.local_id,
                                remote_id,
                                base_remote_id: old_object.remote_id,
                            old_hash: old_object.object_hash,
                            old_data: old_object.object_data,
                            new_hash: new_object.object_hash,
                            new_data: new_object.object_data
                        });
                        if (changeset !== null) {
                            object.object_hash = changeset.new_hash;
                        }
                    }
                    if (local_id === undefined) {
                        local_id = object.local_id;
                    }
                    log.debug("Context.update_object_in_database.transaction.objects.modify.handler out");
                });
            })
            .catch((error) => {
                if ("failedKeys" in error) {
                    log.error("Context.update_object_in_database.transaction error: ", error, " when was updating keys ", error.failedKeys);
                    error.failures.forEach((failure, index) => {
                        log.error(`Context.update_object_in_database.transaction failure ${index}: `, failure);
                    });
                } else {
                    log.error("Context.update_object_in_database.transaction error: ", error);
                }
                throw error;
            })
            .then((modified) => {
                log.debug(`Context.update_object_in_database.transaction modified =${modified}`);
                if (modified === 0) {
                    log.debug(`Context.update_object_in_database.transaction no objects has been updated with id local_id = ${local_id} and remote_id = ${remote_id}`);
                    return;
                }
                if (remote_id === undefined) {
                    return space.push();
                }
            })
            .then(() => new_object);
    };

    /**
     * Modify a given object in the database
     * @param {*} key 
     * @param {*} value 
     * @param {*} time 
     * @returns 
     */
    this.object_update_path = function (key, value, time) {
        log.debug(`Context.object_update_path key = ${key}`, {value}, {time});
        let new_object = {};
        let objects = database.objects.where(key);
        log.debug("Context.object_update_path found objects = ", objects);
        let old_object = {};
        return objects
            .modify((object) => {
                old_object = Dexie.deepClone(object);
                for (let path in value) {
                    let new_value = value[path];
                    if (typeof new_value === "function") {
                        let old_value = Dexie.getByKeyPath(object, path);
                        //log.debug("old_value ", old_value);
                        new_value = value[path](old_value);
                        //log.debug("new_value ", new_value);
                    }
                    for (let path_time in time) {
                        let new_time = time[path_time];
                        if (typeof new_time === "function") {
                            let old_time = Dexie.getByKeyPath(object, path_time);
                            new_time = time[path_time](old_time);
                            //log.debug("old_time ", old_time);
                        }
                        //log.debug("new_time ", new_time);
                        Dexie.setByKeyPath(object, path_time, new_time);
                    }
                    Dexie.setByKeyPath(object, path, new_value);
                }
                log.debug("space.request_conflict_ongoing ", space.request_conflict_ongoing);
                Object.assign(new_object, object);
                let changeset = object_index.commit({
                        local_id: object.local_id,
                        remote_id: undefined,
                        base_remote_id: old_object.remote_id,
                    old_hash: old_object.object_hash,
                    old_data: old_object.object_data,
                    new_hash: new_object.object_hash,
                    new_data: new_object.object_data
                });
                if (changeset !== null) {
                    object.object_hash = changeset.new_hash;
                }
            })
            .catch((error) => {
                if ("failedKeys" in error) {
                    log.error("Context.object_update_path error: ", error, " when was updating keys ", error.failedKeys);
                    error.failures.forEach((failure, index) => {
                        log.error(`Context.object_update_path failure ${index}: `, failure);
                    });
                } else {
                    log.error("Context.object_update_path error: ", error);
                }
                throw error;
            })
            .then((modified) => {
                if (modified === 0) {
                    return;
                }
                log.debug("Context.object_update_path modifier -> push");
                return space.push();
            })
            .then(() => new_object);
    };
    this.object_path = function ({key}) {
        return database.objects.where(key);
    };
    this.object_retrieve_path = function ({key}) {
        return database.objects.where(key).notEqual("");
    };
    this.object_retrieve = function ({local_id, remote_id}) {
        log.debug("Context.object_retrieve in", {local_id, remote_id});
        let objects = database.objects;
        if (local_id !== undefined) {
            objects = objects.where("local_id").equals(local_id);
        }
        if (remote_id !== undefined) {
            objects = objects.where("remote_id").equals(remote_id);
        }
        if (local_id === undefined && remote_id === undefined) {
            objects = objects.toCollection();
        }
        log.debug("Context.object_retrieve out", objects);
        return objects;
    };
    this.object_revert_update = function ({local_id, remote_id, old_data, old_hash, new_hash}) {
        let old_object = {};
        let objects = database.objects;
        if (local_id !== undefined) {
            objects = objects.where("local_id").equals(local_id);
        } else if (remote_id !== undefined) {
            objects = objects.where("remote_id").equals(remote_id);
        }
        if (remote_id !== undefined) {
            old_object.remote_id = remote_id;
        }
        if (old_hash !== undefined) {
            old_object.object_hash = old_hash;
        }
        if (old_data !== undefined) {
            object_set_data(old_object, old_data);
        }
        return database
            .transaction("rw", database.objects, database.configurations, () => {
                return objects.modify((object) => {
                if (object.object_hash !== new_hash) {
                    return;
                }
                Object.assign(object, old_object);
            });
            })
            .catch((error) => {
            if ("failedKeys" in error) {
                log.error("Context.object_revert_update error: ", error, " when was updating keys ", error.failedKeys);
                error.failures.forEach((failure, index) => {
                    log.error(`Context.object_revert_update failure ${index}: `, failure);
                });
            } else {
                log.error("Context.object_revert_update error: ", error);
            }
            throw error;
            })
            .then((modified) => {
            if (modified === 0) {
                log.error(`Context.object_revert_update no object modified on reverting update on local_id = ${local_id} and remote_id = ${remote_id}`);
                return;
            }
            })
            .then(() => old_object);
    };
    this.object_revert_create = function ({local_id, remote_id}) {
        let old_object = {};
        let objects = database.objects;
        if (local_id !== undefined) {
            objects = objects.where("local_id").equals(local_id);
        } else if (remote_id !== undefined) {
            objects = objects.where("remote_id").equals(remote_id);
        } else {
            throw "local id or remote id must be defined";
        }
        
        return database
            .transaction("rw", database.objects, database.configurations, () => {
            return objects.modify((object, ref) => {
                Object.assign(old_object, object);
                delete ref.value;
            });
            })
            .catch((error) => {
            log.error("Context.object_revert_create error: ", error);
            throw error;
            })
            .then((modified) => {
            if (modified === 0) {
                log.error(`Context.object_revert_create failed on local_id = ${local_id} and remote_id = ${remote_id}`);
                return;
            }
            })
            .then(() => old_object);
    };
    this.object_revert_delete = function ({local_id, remote_id, object_data, object_hash}) {
        let new_object = {};
        if (object_data === undefined) {
            throw "object data shall be defined";
        }
        if (local_id === undefined && remote_id === undefined) {
            throw "object not identified";
        }
        if (local_id !== undefined) {
            new_object.local_id = local_id;
        } else {
            new_object.local_id = `local-${uuid()}`;
        }
        if (remote_id !== undefined) {
            new_object.remote_id = remote_id;
        }
        if (object_hash !== undefined) {
            new_object.object_hash = object_hash;
        }
        object_set_data(new_object, object_data);
        return database
            .transaction("rw", database.objects, database.configurations, () => {
            return database.objects.put(new_object);
            })
            .catch((error) => {
            log.error("Context.object_revert_delete error: ", error);
            throw error;
            })
            .then(() => {
            return new_object;
        });
    };

    this.object_handle_notify = async (object) => {
        if (room === null) {
            return;
        }
        if (object.new_hash !== null && object.old_hash !== null) {
            return this.update_object_in_database({
                remote_id: object.remote_id, 
                object_data: object.data,
                object_hash: object.new_hash
            });
        } else if (object.new_hash !== null) {
            return this.create_object_in_database({
                remote_id: object.remote_id,
                object_data: object.data,
                object_hash: object.new_hash
            });
        } else if (object.old_hash !== null) {
            return this.delete_object_in_database({
                remote_id: object.remote_id
            });
        }
    };

    this.configuration_create = function ({name, data, hash}) {
        log.debug("Context.configuration_create", {name, data});
        let new_configuration = {};
        if (name !== undefined) {
            new_configuration.name = name;
        }
        if (data !== undefined) {
            new_configuration.data = data;
        }
        if (hash !== undefined) {
            new_configuration.hash = hash;
        }
        return database.configurations
            .put(new_configuration)
            .catch((error) => {
            log.error("Context.configuration_create error: ", error);
            throw error;
            })
            .then(() => {
            if (hash === undefined) {
                configuration_index.commit(new_configuration.name, null, new_configuration);
                return space.push();
            }
            })
            .then(() => new_configuration);
    };
    //this.configuration_update_actions = [];
    this.configuration_update = function ({name, data, hash}) {
        log.debug("Context.configuration_update", {name, data});
        let new_configuration = {};
        let configurations = database.configurations;
        if (name !== undefined) {
            configurations = configurations.where("name").equals(name);
        }
        if (data !== undefined) {
            new_configuration.data = data;
        }
        if (hash !== undefined) {
            new_configuration.hash = hash;
        }
        return database
            .transaction("rw", database.configurations, database.objects, () => {
            return configurations.modify((configuration) => {
                let old_configuration = {};
                Object.assign(old_configuration, configuration);
                Object.assign(configuration, new_configuration);
                Object.assign(new_configuration, configuration);
                if (hash === undefined && data !== undefined) {
                    configuration_index.commit(new_configuration.name, old_configuration, new_configuration);
                }
            });
            })
            .catch((error) => {
            if ("failedKeys" in error) {
                log.error("Context.configuration_update error: ", error, " when was updating keys ", error.failedKeys);
                error.failures.forEach((failure, index) => {
                    log.error(`Context.configuration_update failure ${index}: `, failure);
                });
            } else {
                log.error("Context.configuration_update error: ", error);
            }
            throw error;
            })
            .then(() => {
            if (hash === undefined) {
                return space.push();
            }
            })
            .then(() => new_configuration);
    };
    this.configuration_retrieve = function ({name}) {
        if (name === undefined) {
            return database.configurations.toCollection();
        }
        return database.configurations.get(name);
    };
    this.configuration_delete = function ({name, hash}) {
        log.error("Context.configuration_delete", {name, hash});
        let configurations = database.configurations;
        if (name !== undefined) {
            configurations = configurations.where("name").equals(name);
        } else {
            configurations = configurations.toCollection();
        }
        return configurations
            .modify((configuration, ref) => {
            if (hash === undefined) {
                configuration_index.commit(configuration.name, configuration, null);
            }
            delete ref.value;
            })
            .catch((error) => {
            log.error("Context.configuration_delete error:", error);
            throw error;
            })
            .then(() => {
            if (hash === undefined) {
                return space.push();
            }
        });
    };
    this.configuration_revert_update = function ({name, data, hash}) {
        let new_configuration = {};
        let configurations = database.configurations;
        if (name !== undefined) {
            configurations = configurations.where("name").equals(name);
        } else {
            configurations = configurations.toCollection();
        }
        if (data !== undefined) {
            new_configuration.data = data;
        }
        if (hash !== undefined) {
            new_configuration.hash = hash;
        }
        return configurations
            .modify((configuration) => {
            let old_configuration = {};
            Object.assign(old_configuration, configuration);
            Object.assign(configuration, new_configuration);
            Object.assign(new_configuration, configuration);
            })
            .catch((error) => {
            if ("failedKeys" in error) {
                log.error("Context.configuration_revert_update error:", error, "when was updating keys", error.failedKeys);
                error.failures.forEach((failure, index) => {
                    log.error(`Context.configuration_revert_update failure ${index}:`, failure);
                });
            } else {
                log.error("Context.configuration_revert_update error:", error);
            }
            throw error;
            })
            .then(() => new_configuration);
    };
    this.configuration_revert_create = function ({name}) {
        let configurations = database.configurations;
        if (name !== undefined) {
            configurations = configurations.where("name").equals(name);
        } else {
            configurations = configurations.toCollection();
        }
        return configurations
            .modify((configuration) => {
            delete configuration.data;
            })
            .catch((error) => {
            log.error("Context.configuration_revert_create error: ", error);
            throw error;
        });
    };
    this.configuration_revert_delete = function ({name, data, hash}) {
        let new_configuration = {};
        if (name !== undefined) {
            new_configuration.name = name;
        }
        if (data !== undefined) {
            new_configuration.data = data;
        }
        if (hash !== undefined) {
            new_configuration.hash = hash;
        }
        return database.configurations
            .put(new_configuration)
            .catch((error) => {
            log.error("Context.configuration_revert_delete error:", error);
            throw error;
            })
            .then(() => new_configuration);
    };
    
    this.configuration_handle_notify = async (configuration) => {
        if (room === null) {
            return;
        }
        if (configuration.new_hash !== null && configuration.old_hash !== null) {
            return this.configuration_update({
                name: configuration.name, 
                data: configuration.data,
                hash: configuration.new_hash
            });
        } else if (configuration.new_hash !== null) {
            return this.configuration_create({
                name: configuration.name,
                data: configuration.data,
                hash: configuration.new_hash
            });
        } else if (configuration.old_hash !== null) {
            return this.configuration_delete({
                name: configuration.name,
                hash: configuration.old_hash
            });
        }
    };
    
    this.message_create = function (message) {
        return space.message_create(message);
    };
    this.message_update = function (message) {
        return space.message_update(message);
    };
    this.message_retrieve = function (message) {
        return space.message_retrieve(message);
    };
    this.message_delete_all = function (message) {
        return space.message_delete_all(message);
    };
    this.message_delete = function (message) {
        return space.message_delete(message);
    };
    this.message_empty = function () {
        return space.message_empty();
    };

    this.open = async function () {
        context_data_store = await global.configuration.add_store(data_store_name({space_index: space.index, desktop_index}), {});
        context_data = context_data_store.export();
        await database.open();
        hooks.bind(database);
        await object_index.init(null);
        await projection.init();
        return this;
    };
    this.close = async function () {
        hooks.unbind(database);
        await projection.deinit();
        await database.close();
    };
    this.delete = async function () {
        // delete media in database (before context is deleted in database)
        let map_media_id = await get_map_object_attribute("dataMedia");
        let objects = database.objects;
        if (map_media_id != undefined) {
            let type = await get_map_object_attribute("dataType");
            if (type === "gis") {
                // delete gis fallback image medias
                // TODO : this.delete_fallback_medias(map_media_id); depends on gis type (Google mpa, OSM,...)
                let media_objects = objects.where("object_data.attributes.dataMedia").equals(map_media_id);
                await media_objects.each((media) => {
                    log.debug("Context.delete media ", media.object_data.attributes);
                    if (media.object_data.attributes.dataMediaLayer1 != undefined) {
                        global.storage.selected_space.media_delete({local_id: media.object_data.attributes.dataMediaLayer1});
                    }
                    if (media.object_data.attributes.dataMediaLayer2 != undefined) {
                        global.storage.selected_space.media_delete({local_id: media.object_data.attributes.dataMediaLayer2});
                    }
                    if (media.object_data.attributes.dataMediaLayer3 != undefined) {
                        global.storage.selected_space.media_delete({local_id: media.object_data.attributes.dataMediaLayer3});
                    }
                });
            } else {
                let media_objects = objects.where("object_data.attributes.dataMedia").equals(map_media_id);
                await media_objects.each((media) => {
                    if (media.object_data.attributes.dataMedia != undefined) {
                        global.storage.selected_space.media_delete({local_id: media.object_data.attributes.dataMedia});
                    }
                });
            }
        }

        hooks.unbind(database);
        await projection.deinit();
        await context_data_store.delete();
        await database.delete();
    };
    this.duplicate = async function (new_space_index, new_desktop_index) {
        if (this.space.index === new_space_index && this.index === new_desktop_index) {
            return;
        }
        const new_database = new Dexie(Context.database_name({space_index: new_space_index, desktop_index: new_desktop_index}));
        const schema = database.tables.reduce((result, table) => {
            result[table.name] = [table.schema.primKey]
                .concat(table.schema.indexes)
                .map((indexSpec) => indexSpec.src)
                .toString();
            return result;
        }, {});
        new_database.version(database.verno).stores(schema);
        
        await database.tables.reduce((prev, table) => {
            return prev.then(() => table.toArray()).then((rows) => new_database.table(table.name).bulkPut(rows));
        }, Promise.resolve());

        let new_context_data_store = data_store_name({space_index: new_space_index, desktop_index: new_desktop_index});
        let new_context_data = Dexie.deepClone(this.data);
        await global.configuration.add_store(new_context_data_store, new_context_data);
        await new_database.close();
    };

    this.on = (table_name, event_name, handler) => {
        hooks.on(table_name, event_name, handler);
    };

    this.off = (table_name, event_name, handler) => {
        hooks.off(table_name, event_name, handler);
    };


    /**
     * Dump the content of database.objects
     * @returns a string with all objects
     */
    this.dump = async () => {
        log.debug("Context.dump_objects: List of objects in indexedDB current context:");
        let str = "";
        await database.objects.toArray()
        .then(list => {
            list.forEach( item => {
                str = str + "{" + JSON.stringify(item) + "}";
                //log.debug("Context.dump_objects", item);
            });
        });
        if (str == "") {
            str = "call context.dump_objects with await !";
        }
        return str;
    };

    this.dump_configurations = () => {
        log.debug("Context.dump_configurations IN");
        database.configurations.toArray()
        .then(list => {
            list.forEach( item => {
                log.debug(`Context.dump_configurations ${JSON.stringify(item)}`);
            });
        });
    };
};
Context.data_store_name = data_store_name;
Context.data_store = data_store;
Context.database_name = database_name;

export default Context;
